/*
  <maratona-price-range> — slider de duas bolinhas para o filtro de preço
  ----------------------------------------------------------------------
  Como funciona:
  - os dois <input type="range"> são só o controle; quem vale para o filtro
    são os campos de texto do Dawn (De/Para), que já carregam o name correto;
  - enquanto arrasta, o componente atualiza os campos SEM disparar evento
    (atribuir .value não dispara nada), então a página não recarrega a cada pixel;
  - ao soltar, dispara 'input' com bubbles, que é o evento que o facets.js do
    Dawn escuta no formulário para refazer a busca.

  Se o JS não rodar, o cliente continua com os campos De/Para funcionando.
*/
class MaratonaPriceRange extends HTMLElement {
  connectedCallback() {
    this.trilho = this.querySelector('.maratona-price__fill');
    this.rangeMin = this.querySelector('[data-range="min"]');
    this.rangeMax = this.querySelector('[data-range="max"]');
    this.saidaMin = this.querySelector('[data-saida="min"]');
    this.saidaMax = this.querySelector('[data-saida="max"]');

    // Campos de texto originais do Dawn (o primeiro é o "De", o segundo o "Para")
    const campos = this.querySelectorAll('input[type="text"]');
    this.campoMin = campos[0];
    this.campoMax = campos[1];

    if (!this.rangeMin || !this.rangeMax || !this.campoMin || !this.campoMax) return;

    this.teto = Number(this.rangeMax.max);
    this.formatador = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: this.dataset.moeda || 'BRL',
      maximumFractionDigits: 0,
    });

    // Estado inicial: o que já estiver aplicado na URL do filtro
    this.rangeMin.value = this.paraNumero(this.campoMin.value, 0);
    this.rangeMax.value = this.paraNumero(this.campoMax.value, this.teto);
    this.desenhar();

    // Só agora os campos De/Para podem sumir: se o JS falhasse antes daqui,
    // a cliente continuaria com eles na tela para filtrar na mão
    this.classList.add('maratona-price--pronto');

    // Arrastar: só atualiza a tela
    this.rangeMin.addEventListener('input', this.aoArrastar.bind(this, 'min'));
    this.rangeMax.addEventListener('input', this.aoArrastar.bind(this, 'max'));

    // Soltar: aí sim refaz a busca
    this.rangeMin.addEventListener('change', this.aoSoltar.bind(this));
    this.rangeMax.addEventListener('change', this.aoSoltar.bind(this));

    // Quem prefere digitar o valor continua podendo: o slider se ajusta
    this.campoMin.addEventListener('change', this.aoDigitar.bind(this));
    this.campoMax.addEventListener('change', this.aoDigitar.bind(this));
  }

  /* Converte "1.234,56" (jeito brasileiro) em número. Vazio vira o padrão. */
  paraNumero(texto, padrao) {
    if (!texto) return padrao;
    const limpo = String(texto).replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '');
    const n = Math.round(Number(limpo));
    return Number.isFinite(n) ? Math.min(Math.max(n, 0), this.teto) : padrao;
  }

  /* Impede que uma bolinha passe da outra e redesenha a faixa colorida */
  aoArrastar(qual) {
    let min = Number(this.rangeMin.value);
    let max = Number(this.rangeMax.value);

    if (min > max) {
      // empurra a outra bolinha em vez de travar, que é o comportamento esperado
      if (qual === 'min') max = min;
      else min = max;
      this.rangeMin.value = min;
      this.rangeMax.value = max;
    }

    this.desenhar();
  }

  /* Escreve nos campos do Dawn e avisa o formulário */
  aoSoltar() {
    const min = Number(this.rangeMin.value);
    const max = Number(this.rangeMax.value);

    // extremos ficam vazios pra não sujar a URL com filtro que não filtra nada
    this.campoMin.value = min > 0 ? String(min) : '';
    this.campoMax.value = max < this.teto ? String(max) : '';

    this.campoMin.dispatchEvent(new Event('change', { bubbles: true }));
    this.campoMax.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /* Caminho inverso: digitou no campo, move a bolinha */
  aoDigitar() {
    this.rangeMin.value = this.paraNumero(this.campoMin.value, 0);
    this.rangeMax.value = this.paraNumero(this.campoMax.value, this.teto);
    this.aoArrastar('min');
  }

  desenhar() {
    const min = Number(this.rangeMin.value);
    const max = Number(this.rangeMax.value);
    const inicio = (min / this.teto) * 100;
    const fim = (max / this.teto) * 100;

    if (this.trilho) {
      this.trilho.style.left = inicio + '%';
      this.trilho.style.width = Math.max(fim - inicio, 0) + '%';
    }

    if (this.saidaMin) this.saidaMin.textContent = this.formatador.format(min);
    if (this.saidaMax) {
      this.saidaMax.textContent =
        max >= this.teto ? this.formatador.format(this.teto) : this.formatador.format(max);
    }

    // leitor de tela anuncia o valor em reais, não o número cru
    this.rangeMin.setAttribute('aria-valuetext', this.formatador.format(min));
    this.rangeMax.setAttribute('aria-valuetext', this.formatador.format(max));
  }
}

if (!customElements.get('maratona-price-range')) {
  customElements.define('maratona-price-range', MaratonaPriceRange);
}
